import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { createManyHealthRecord, deleteAllHealthRecords } from '../health_services';

const normalizeDate = (value) => {
  if (!value) return '';
  let dateObj;
  if (typeof value === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    dateObj = new Date(excelEpoch.getTime() + value * 86400000);
  } else {
    dateObj = new Date(value);
  }
  return !isNaN(dateObj.getTime()) ? dateObj.toISOString().split('T')[0] : '';
};

const CardExportImport = ({ records, reload }) => {
  const fileInputRef = useRef(null);
  const [importMode, setImportMode] = useState('append');
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const resetState = () => {
    setSelectedFile(null);
    setParsedData([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(records);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'HealthRecords');
    XLSX.writeFile(workbook, 'health_records.xlsx');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(worksheet);
        const normalized = data.map((item) => ({
          ...item,
          date: normalizeDate(item.date),
        }));
        setParsedData(normalized);
        toast.success('File loaded successfully');
      } catch (err) {
        console.error(err);
        toast.error('Failed to read file');
        resetState();
      }
    };

    reader.readAsBinaryString(file);
  };

  const proceedImport = async () => {
    setShowConfirmModal(false);
    setIsImporting(true);
    try {
      await deleteAllHealthRecords();
      await createManyHealthRecord(parsedData);
      toast.success('Imported successfully (override)');
      resetState();
      if (reload) reload();
    } catch (err) {
      console.error(err);
      toast.error('Error importing records');
    } finally {
      setIsImporting(false);
    }
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error('No data to import');
      return;
    }

    if (importMode === 'override') {
      setShowConfirmModal(true);
      return;
    }

    try {
      setIsImporting(true);
      await createManyHealthRecord(parsedData);
      toast.success('Imported successfully (append)');
      resetState();
      if (reload) reload();
    } catch (err) {
      console.error(err);
      toast.error('Error importing records');
    } finally {
      setIsImporting(false);
    }
  };

  const buttonBase = 'px-6 py-3 rounded-xl shadow-md text-white';
  const importDisabled = 'bg-gray-400 cursor-not-allowed';
  const importEnabled = 'bg-emerald-600 hover:bg-emerald-700';

  return (
    <>
      <div className="h-auto bg-white shadow-lg border border-dashed border-gray-300 rounded-xl p-4 flex flex-col gap-4">
        <span className="text-md font-semibold">Export and Import</span>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleExport}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl shadow-md"
          >
            Export Records to Excel
          </button>

          <label className="flex-1 cursor-pointer bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl shadow-md text-center">
            Select Excel to Import
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </div>

        <div className="text-sm text-gray-700">
          Uploaded file: <strong>{selectedFile?.name || 'none'}</strong>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-gray-700 font-semibold">Import Mode:</label>
            <select
              value={importMode}
              onChange={(e) => setImportMode(e.target.value)}
              className="border p-2 rounded-xl shadow-sm"
            >
              <option value="append">Append</option>
              <option value="override">Override</option>
            </select>
          </div>

          <button
            onClick={handleImport}
            disabled={!selectedFile || isImporting}
            className={`${buttonBase} ${
              !selectedFile || isImporting ? importDisabled : importEnabled
            }`}
          >
            {isImporting ? 'Importing...' : 'Perform Import'}
          </button>
        </div>
      </div>

      {/* Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm override</h3>
            <p className="mb-6 text-sm text-gray-700">
              This will delete <strong>all existing records</strong> before importing the new ones. Are you sure?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={proceedImport}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white shadow"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CardExportImport;
